import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Select,
  Input,
  Card,
  Row,
  Col,
  Modal,
  message,
  Space,
  DatePicker,
  Typography,
  Progress,
  Tooltip
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { URL_API } from '@configs/';
import './style.css';
import { showError } from '@components/notification';
import { communityList, convertLocaltimeToUTC, formatCurrency } from '@utils/utils';
import { apiGetOriginalData } from '@services/ManageDevices/DeviceServices';
import { loadFromLocalStorageObjectFromBase64, saveToLocalStorage } from '@databases/localStorage';
import { contantAuthentication } from '@contants/';
import { get, isEmpty } from 'lodash';
import { SelectCommunity } from '@page/Common/SelectCommunity';
import './style.css';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Option } = Select;

const { RangePicker } = DatePicker;

export default function List() {
  const [data, setData] = useState([]);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [loadingFetch, setLoadingFetch] = useState(false);

  const { originalData } = usePermission();

  const renderDenseReading = (value) => {
    if (value === null || value === undefined || value === '') {
      return <div>-</div>;
    }

    return <div>{formatCurrency(Number(value), 3)}</div>;
  };

  const fetchData = async () => {
    try {
      setLoadingFetch(true);
      const res = await apiGetOriginalData({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        fromDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
          : undefined,
        toDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
          : undefined
      });
      setData(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError({ message: 'Something went wrong!' });
      console.log(err);
    } finally {
      setLoadingFetch(false);
    }
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  const onChangeDateRange = (dates, dateStrings) => {
    setDateRange(dates);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!search && !dateRange?.length && currentPage === 1) {
        setCurrentPage(1);
        fetchData();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [search, selectedCommunity, dateRange?.length, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch]);

  const columns = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'id',
      align: 'center',
      width: 80,
      fixed: 'left'
    },

    {
      title: 'Số Serie',
      dataIndex: 'serial_no',
      align: 'center',
      width: 100,
      fixed: 'left'
    },
    {
      title: 'IMEI',
      dataIndex: 'imei',
      align: 'center',
      width: 100
    },
    {
      title: (
        <Tooltip title="Thời gian báo cáo">
          <span>Thời gian báo cáo</span>
        </Tooltip>
      ),
      dataIndex: 'created_at',
      align: 'center',
      render: (text) => (text ? moment(text).format('DD/MM/YYYY HH:mm') : <div>-</div>),
      width: 100
    },
    {
      title: (
        <Tooltip title="Chỉ số đồng hồ (m3)">
          <span>Chỉ số đồng hồ (m3)</span>
        </Tooltip>
      ),
      dataIndex: 'meter_reading',
      align: 'center',
      render: (text) => <div>{formatCurrency(text / 1000.0, 3)}</div>,
      width: 150
    },

    {
      title: (
        <Tooltip title="Số lít/xung (L/P)">
          <span>Số lít/xung (L/P)</span>
        </Tooltip>
      ),
      dataIndex: 'pulse_constant',
      align: 'center',
      width: 150
    },
    {
      title: (
        <Tooltip title="Trạng thái cảnh báo (tại thời điểm truyền)">
          <span>Trạng thái cảnh báo</span>
        </Tooltip>
      ),
      dataIndex: 'alarm_status',
      align: 'center',
      render: (text) =>
        text === 1 ? <div style={{ color: 'red', fontWeight: 'bold' }}>1</div> : <div>0</div>,
      width: 120
    },
    {
      title: (
        <Tooltip title="Cảnh báo từng xảy ra">
          <span>Cảnh báo từng xảy ra</span>
        </Tooltip>
      ),
      dataIndex: 'happended_alarm',
      align: 'center',
      render: (text) =>
        text === 1 ? <div style={{ color: 'red', fontWeight: 'bold' }}>1</div> : <div>0</div>,
      width: 120
    },
    {
      title: (
        <Tooltip title="Cường độ sóng (dBm)">
          <span>Cường độ sóng (dBm)</span>
        </Tooltip>
      ),
      dataIndex: 'rsrp',
      align: 'center',
      render: (text, record) => {
        let rsrp = record.rsrp;
        if (rsrp == null) {
          return <div>-</div>;
        }
        let color = rsrp >= -100 ? 'green' : rsrp >= -110 ? '#ffc107' : 'red';
        let progress =
          rsrp >= -100 ? (rsrp + 100) / 3 + 80 : rsrp >= -110 ? 3 * (rsrp + 110) + 50 : rsrp + 160;
        return (
          <Progress
            percentPosition={{ align: 'start', type: 'inner' }}
            size={[100, 20]}
            strokeColor={color}
            percent={progress}
            showInfo
            format={() => `${rsrp.toString()}`}
          />
        );
      },
      width: 150
    },
    {
      title: (
        <Tooltip title="Bắt đầu ghi">
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 120,
              display: 'inline-block'
            }}>
            Bắt đầu ghi
          </span>
        </Tooltip>
      ),
      dataIndex: 'denseData_startCollectionTime',
      align: 'center',
      width: 120
    },
    {
      title: (
        <Tooltip title="Chỉ số ban đầu (xung)">
          <span>Chỉ số ban đầu (xung)</span>
        </Tooltip>
      ),
      dataIndex: 'denseData_startFlowPulseNumber',
      width: 120
    },

    {
      title: 'Tiêu thụ trong khoảng thời gian (m3)',
      align: 'center',
      children: [
        {
          title: (
            <Tooltip title="Tiêu thụ trong khoảng thời gian phút thứ 0 đến phút thứ 15">
              <span>0-15</span>
            </Tooltip>
          ),
          dataIndex: 'consumption_00_15',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Tiêu thụ trong khoảng thời gian phút thứ 15 đến phút thứ 30">
              <span>15-30</span>
            </Tooltip>
          ),
          dataIndex: 'consumption_15_30',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Tiêu thụ trong khoảng thời gian phút thứ 30 đến phút thứ 45">
              <span>30-45</span>
            </Tooltip>
          ),
          dataIndex: 'consumption_30_45',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Tiêu thụ trong khoảng thời gian phút thứ 45 đến phút thứ 60">
              <span>45-60</span>
            </Tooltip>
          ),
          dataIndex: 'consumption_45_60',
          align: 'center',
          render: renderDenseReading,
          width: 120
        }
      ]
    },
    {
      title: 'Chỉ số đồng hồ tại các thời điểm (m3)',
      align: 'center',
      children: [
        {
          title: (
            <Tooltip title="Chỉ số đồng hồ tại thời điểm bắt đầu ghi">
              <span>0</span>
            </Tooltip>
          ),
          dataIndex: 'meter_reading_00',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Chỉ số đồng hồ sau 15 phút">
              <span>15</span>
            </Tooltip>
          ),
          dataIndex: 'meter_reading_15',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Chỉ số đồng hồ sau 30 phút">
              <span>30</span>
            </Tooltip>
          ),
          dataIndex: 'meter_reading_30',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Chỉ số đồng hồ sau 45 phút">
              <span>45</span>
            </Tooltip>
          ),
          dataIndex: 'meter_reading_45',
          align: 'center',
          render: renderDenseReading,
          width: 120
        },
        {
          title: (
            <Tooltip title="Chỉ số đồng hồ sau 60 phút">
              <span>60</span>
            </Tooltip>
          ),
          dataIndex: 'meter_reading_60',
          align: 'center',
          render: renderDenseReading,
          width: 120
        }
      ]
    },
    {
      title: (
        <Tooltip title="Dữ liệu dày đặc (Số xung)">
          <span>Dữ liệu dày đặc (Số xung)</span>
        </Tooltip>
      ),
      dataIndex: 'denseData_diffirenceOfTheFlowPulseNumber',
      align: 'center',
      render: (text) => (text != null ? <div>{text.toString()}</div> : <div>-</div>),
      width: 150
    },

    {
      title: 'Điện áp (V)',
      dataIndex: 'voltage',
      align: 'center',
      width: 100
    },
    // {
    //   title: (
    //     // <Tooltip title="Tỷ lệ tín/tạp (dB)">
    //     //   <span
    //     //     style={{
    //     //       whiteSpace: 'nowrap',
    //     //       overflow: 'hidden',
    //     //       textOverflow: 'ellipsis',
    //     //       maxWidth: 100,
    //     //       display: 'inline-block'
    //     //     }}>
    //     //     Tỷ lệ tín/tạp (dB)
    //     //   </span>
    //     // </Tooltip>
    //     <Tooltip title="Tỷ lệ tín/tạp (dB)">
    //       <span>Tỷ lệ tín/tạp (dB)</span>
    //     </Tooltip>
    //   ),
    //   dataIndex: 'snr',
    //   align: 'center',
    //   width: 100
    // },
    {
      title: (
        <Tooltip title="Số lần rớt mạng">
          <span>Số lần rớt mạng</span>
        </Tooltip>
      ),
      dataIndex: 'number_offline',
      align: 'center',
      width: 120
    }
  ]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      message.warning('Vì dữ liệu quá lớn, Vui lòng chọn ngày để xuất đữ liệu!');
      return;
    }
    let user = loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH);
    if (!isEmpty(user)) {
      user = JSON.parse(user);
    }
    setLoading(true);
    const response = await axios.get(`${URL_API}/api/original-data/export-report`, {
      params: {
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        fromDate: convertLocaltimeToUTC(dateRange[0], '00:00:00.000'),
        toDate: convertLocaltimeToUTC(dateRange[1], '23:59:59.999')
      },
      headers: {
        Authorization: `Bearer ${user?.accessToken}`
      },
      responseType: 'blob'
    });
    const dateKey = moment().format('YYYYMMDD.HHmmss');
    const type = response.headers['content-type'];
    const blob = new Blob([response.data], { type: type });
    const link1 = document.createElement('a');
    link1.href = window.URL.createObjectURL(blob);
    link1.setAttribute('download', `Thong_ke_du_lieu_goc_${dateKey}.xlsx`);
    document.body.appendChild(link1);
    link1.click();
    setLoading(false);
  };

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0051A9' }}>
            Dữ liệu gốc
          </Typography.Title>
        </Col>
        {originalData.export && (
          <Col>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportExcelButtonClick}
              loading={loading}
              style={{
                color: '#fff',
                height: '36px',
                backgroundColor: '#0051A9',
                borderColor: '#0051A9',
                fontSize: '18px'
              }}>
              Xuất báo cáo
            </Button>
          </Col>
        )}
      </Row>
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]}>
          <Col md={6} xs={12}>
            <Input
              style={{ height: '36px', width: '100%', fontSize: '18px' }}
              size="small"
              placeholder="Tìm kiếm theo số serial, imei..."
              value={search}
              onChange={handleSearchChange}
              onPressEnter={() => {
                setCurrentPage(1);
                setFlagSearch(!flagSearch);
              }}
            />
          </Col>
          <Col md={6} sm={12}>
            <RangePicker
              value={dateRange}
              onChange={onChangeDateRange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Col>
          <Col md={6} xs={12}>
            <SelectCommunity value={selectedCommunity} onChange={setSelectedCommunity} />
          </Col>
          <Col md={3} xs={6}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                setCurrentPage(1);
                setFlagSearch(!flagSearch);
              }}
              style={{
                height: '36px',
                backgroundColor: '#0051A9',
                borderColor: '#0051A9',
                fontSize: '18px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
      </Card>
      <Table
        bordered
        loading={loadingFetch}
        dataSource={data || []}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: handleChangePage,
          onShowSizeChange: handleChangePageSize,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        style={{
          borderRadius: 0,
          thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
          fontSize: '30px'
        }}
        scroll={{ x: 'max-content', y: 600 }}
      />
    </div>
  );
}
